import { useMutation } from '@apollo/client'
import React, { useState, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { useParams } from 'react-router-dom'
import SignatureCanvas from 'react-signature-canvas'
import { toast } from 'react-toastify'
import { UPDATE_FORM_BUILDER_BY_FORM_STRING_ID } from '../../../graphql/mutation'
import { GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID } from '../../../graphql/query'
import { CANVAS_BASIC_INPUT_TYPES } from './formFieldTypes'
import RecipientNameLabelIcon from './RecipientNameLabelIcon'
import TableFieldPreview from './TableFieldPreview'
import ButtonRowPreview from './ButtonRowPreview'
import { isStandaloneButton } from './buttonFieldUtils'
import { usesPrefillEditFieldsMode } from './prefillFieldSettingsUtils'
import { ensureUniqueFormFieldIds } from './tableFieldUtils'
interface IProps {
  formElements: any
  setFormElements: any
  setSelectedFields: any
  selectedField: any
  formJson: any
  isFormTimed: any
  togglerFieldIndex: any
  setTogglerFieldIndex: any
  subFormFieldIndexes: any
  setSubFormFieldIndexes: any
  currentPageIndex: number
  setCurrentPageIndex: any
  allFormElements: any
  isPrefillForm?: boolean
  selectedTableColumnIndex?: number | null
  onSelectTableColumn?: (columnIndex: number | null) => void
  selectedTableRowIndex?: number | null
  onSelectTableRow?: (rowIndex: number | null) => void
}
interface CaptchaProps {
  onRefresh: (newCaptcha: string) => void
  inputTextName: any
  inputTextNameColor: any
}
interface RatingsProps {
  validation: any
  label: string
  color: string
  alignment: any
}

const FormPage: React.FC<IProps> = ({
  formElements,
  setFormElements,
  setSelectedFields,
  selectedField,
  formJson,
  isFormTimed,
  togglerFieldIndex,
  setTogglerFieldIndex,
  subFormFieldIndexes,
  setSubFormFieldIndexes,
  currentPageIndex,
  setCurrentPageIndex,
  allFormElements,
  isPrefillForm = false,
  selectedTableColumnIndex = null,
  onSelectTableColumn,
  selectedTableRowIndex = null,
  onSelectTableRow
}) => {
  const safeFormElements = Array.isArray(formElements) ? formElements : []

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [pageToDelete, setPageToDelete] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const params = useParams()
  const uuid = params.uuid

  // Add the save mutation for auto-save functionality
  const [SaveFormJson] = useMutation(UPDATE_FORM_BUILDER_BY_FORM_STRING_ID, {
    refetchQueries: [
      {
        query: GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID,
        variables: {
          input: {
            formUuid: uuid
          }
        },
        context: {
          headers: {
            'access-control-allow-methods': 'Form-Builder'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // Logic to handle form submission
  }
  const onDragEnd = (result: any) => {
    if (!result.destination) return // Dragged outside the list

    const updatedFormElements = Array.from(safeFormElements)
    const [movedElement] = updatedFormElements.splice(result.source.index, 1)
    updatedFormElements.splice(result.destination.index, 0, movedElement)

    const newAllFormElements = [...allFormElements]
    newAllFormElements[currentPageIndex] = updatedFormElements
    setFormElements(newAllFormElements)
    setSelectedFields(result.destination.index)

    // setFormElements(updatedFormElements)
    // setSelectedFields(result.destination.index)
  }

  // Auto-save function
  const autoSaveAfterDelete = async (updatedFormElements: any[]) => {
    try {
      const uniqueFormData = ensureUniqueFormFieldIds(
        Array.isArray(updatedFormElements?.[0]) ? updatedFormElements : [updatedFormElements]
      )
      const inputRes = await SaveFormJson({
        variables: {
          input: {
            formUuid: uuid,
            cardStyle: formJson?.cardStyle,
            cardBackground: formJson?.cardBackground,
            featuresToUpdate: {
              formData: uniqueFormData
            }
          }
        }
      })

      if (inputRes.data.updateFormBuilderByFormStringId.message) {
        toast.success(`Page deleted successfully!`)
      }

      return true
    } catch (err) {
      toast.error('Page deleted but failed to auto-save. Please save manually.')
      return false
    }
  }

  const handleDeleteConfirmed = async () => {
    if (pageToDelete === null) return

    if (allFormElements.length <= 1) {
      toast.warning('Cannot delete the last page. At least one page is required.')
      setShowDeleteModal(false)
      setPageToDelete(null)
      return
    }

    setIsDeleting(true)

    try {
      // Create updated form elements without the deleted page
      const updatedFormElements = allFormElements.filter(
        (_: any, index: number) => index !== pageToDelete
      )

      // Update the form elements state
      setFormElements(updatedFormElements)

      // Handle current page index adjustment
      let newCurrentPageIndex = currentPageIndex
      if (currentPageIndex >= updatedFormElements.length) {
        newCurrentPageIndex = updatedFormElements.length - 1
      } else if (currentPageIndex === pageToDelete && pageToDelete > 0) {
        newCurrentPageIndex = pageToDelete - 1
      }

      setCurrentPageIndex(newCurrentPageIndex)

      // Clear selected field if we're deleting the current page
      if (currentPageIndex === pageToDelete) {
        setSelectedFields(null)
      }

      // Auto-save the changes
      await autoSaveAfterDelete(updatedFormElements)
    } catch (error) {
      toast.error('Failed to delete page. Please try again.')
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
      setPageToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
    setPageToDelete(null)
  }

  const handleFieldClick = (index: number) => {
    safeFormElements[index].includeOnCondition?.conditionActive
      ? setTogglerFieldIndex(safeFormElements[index].includeOnCondition.basedOnElementOfIndex)
      : setTogglerFieldIndex(-1)
    safeFormElements[index].type === 'subform'
      ? setSubFormFieldIndexes(
          safeFormElements[index].subformFields.map((field: any) => field.copiedFromFieldAtFieldId)
        )
      : setSubFormFieldIndexes([])
    onSelectTableColumn?.(null)
    onSelectTableRow?.(null)
    setSelectedFields(index)
  }

  const handleFileUpload = (index: any, event: any) => {
    const file = event.target.files[0]
    console.log('Uploaded file:', file)
  }
  const handleCaptchaRefresh = (index: number, newCaptcha: string) => {
    const updatedFormElements = [...formElements]
    if (
      updatedFormElements[index] &&
      updatedFormElements[index].formInput &&
      Array.isArray(updatedFormElements[index].formInput.options)
    ) {
      updatedFormElements[index].formInput.options[0].value = newCaptcha
      setFormElements(updatedFormElements)
    } else {
      console.error('Unexpected structure in safeFormElements')
    }
  }
  const Captcha: React.FC<CaptchaProps> = ({ onRefresh, inputTextName, inputTextNameColor }) => {
    const [captcha, setCaptcha] = useState('')
    useEffect(() => {
      setCaptcha(generateRandomCaptcha())
    }, [])
    function generateRandomCaptcha() {
      const array = new Uint32Array(1)
      window.crypto.getRandomValues(array)
      return array[0].toString(36).slice(0, 6).toUpperCase()
    }
    const handleRefresh = () => {
      const newCaptcha = generateRandomCaptcha()
      setCaptcha(newCaptcha)
      if (onRefresh) {
        onRefresh(newCaptcha)
      }
    }

    return (
      <div className='d-flex flex-column flex-grow-1 ms-sm-4'>
        <div className='d-flex align-items-center gap-2'>
          <div className='border rounded p-2 bg-light flex-grow-1 text-center'>
            <span
              className='fs-4 fw-bold'
              style={{ fontFamily: 'monospace', letterSpacing: '0.3em' }}
            >
              {captcha}
            </span>
          </div>
          <button type='button' className='btn btn-light btn-icon' onClick={handleRefresh}>
            <i className='fas fa-sync-alt'></i>
          </button>
        </div>
        <div className='mt-3'>
          <label
            htmlFor='captchaInput'
            className='form-label'
            style={{ color: inputTextNameColor }}
          >
            {inputTextName}
          </label>
          <input type='text' id='captchaInput' className='form-control' />
        </div>
      </div>
    )
  }
  const Ratings: React.FC<RatingsProps> = ({ validation, label, color, alignment }) => {
    const [rating, setRating] = useState(0)
    const justifyContent =
      alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start'

    return (
      <div style={{ textAlign: alignment }}>
        <label
          className={`fw-semibold mb-2 fs-5 ${
            !!validation && validation[0]?.rule.includes('required') ? 'required' : ''
          }`}
          style={{ color: color }}
        >
          {label}
        </label>
        <div className='d-flex' style={{ justifyContent }}>
          <div className='d-inline-flex fs-1'>
            {[1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                className='star'
                onClick={() => setRating(index)}
                style={{
                  margin: '0 4px',
                  cursor: 'pointer',
                  color: rating >= index ? '#FFC107' : '#e0e0e0',
                  transition: 'color 0.2s'
                }}
              >
                ★
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const confirmDeletePage = (index: number) => {
    setPageToDelete(index)
    setShowDeleteModal(true)
  }

  const conditionalBackgroundColor = formJson?.cardStyle === '' ? '#FFFFFF' : formJson?.cardStyle
  const pageStyle: React.CSSProperties = {
    fontFamily: `"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`
    // backgroundColor: '#f8f9fa' // Soft background for the whole page
  }

  const TimerDisplay: React.FC<{
    timeString: string
  }> = ({ timeString }) => {
    return (
      <div
        style={{
          color: '#6c757d',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 1000
        }}
      >
        {timeString}
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='form-elements' direction='vertical'>
          {(provided: any) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className='d-flex flex-column min-vh-100 py-10'
            >
              <div
                className='w-lg-800px rounded-4 shadow-lg mx-auto'
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: conditionalBackgroundColor
                }}
              >
                {/* Page Header */}
                {allFormElements.length > 1 && (
                  <div className='d-flex justify-content-between align-items-center p-4 border-bottom'>
                    <div className='fw-bold text-muted'>
                      Page {currentPageIndex + 1} of {allFormElements.length}
                    </div>
                    <div className='d-flex gap-2'>
                      <button
                        className='btn btn-sm btn-icon btn-light-danger'
                        onClick={() => confirmDeletePage(currentPageIndex)}
                        title={`Delete current page (${currentPageIndex + 1})`}
                        style={{ width: '35px', height: '35px' }}
                      >
                        {isDeleting ? (
                          <span className='spinner-border spinner-border-sm'></span>
                        ) : (
                          <i className='bi bi-trash fs-5'></i>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Delete Modal */}
                {showDeleteModal && (
                  <div
                    className='modal fade show d-block'
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={handleDeleteCancel}
                  >
                    <div
                      className='modal-dialog modal-dialog-centered'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className='modal-content'>
                        <div className='modal-header'>
                          <h5 className='modal-title'>Confirm Page Deletion</h5>
                          <button
                            type='button'
                            className='btn-close'
                            onClick={handleDeleteCancel}
                          ></button>
                        </div>
                        <div className='modal-body'>
                          <p className='text-gray-700'>
                            Are you sure you want to delete{' '}
                            <strong>page {pageToDelete! + 1}</strong>? This action cannot be undone.
                          </p>
                          <div className='alert alert-danger d-flex align-items-center mt-4'>
                            <i className='bi bi-exclamation-triangle-fill fs-4 me-3'></i>
                            <div>All content on this page will be permanently removed.</div>
                          </div>
                        </div>
                        <div className='modal-footer'>
                          <button
                            type='button'
                            className='btn btn-light'
                            onClick={handleDeleteCancel}
                          >
                            Cancel
                          </button>
                          <button
                            type='button'
                            className='btn btn-danger'
                            onClick={handleDeleteConfirmed}
                          >
                            Delete Page
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Banner Element */}
                {safeFormElements?.find((el: any) => el?.type === 'banner') &&
                  safeFormElements.map(
                    (element: any, index: any) =>
                      element?.type === 'banner' && (
                        <div
                          key={element.id}
                          className='banner'
                          style={{
                            backgroundImage: `url(${element.bannerSrc || ''})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            height: '200px',
                            width: '100%',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleFieldClick(index)}
                        ></div>
                      )
                  )}

                {/* Main Form Content Area */}
                <div className='p-10 p-lg-15'>
                  <form className='form w-100' noValidate onSubmit={handleSubmit}>
                    {safeFormElements?.map(
                      (element: any, index: any) =>
                        element &&
                        !isStandaloneButton(element) &&
                        element.type !== 'banner' && (
                          <Draggable
                            key={element.id}
                            draggableId={(element.id || index).toString()}
                            index={index}
                          >
                            {(providedDraggable: any, snapshot: any) => {
                              let fieldStyle: React.CSSProperties = {
                                // transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                borderRadius: '0.75rem' // 12px
                              }

                              // if (snapshot.isDragging) {
                              //   fieldStyle.transform = 'translateY(-4px)'
                              //   fieldStyle.boxShadow =
                              //     '0 10px 25px rgba(0, 0, 0, 0.1), 0 5px 10px rgba(0, 0, 0, 0.05)'
                              // }

                              if (selectedField === index) {
                                fieldStyle.boxShadow = '0 0 0 3px rgba(13, 110, 253, 0.4)' // Blue glow
                              } else if (
                                element.fieldId === togglerFieldIndex &&
                                safeFormElements[selectedField]?.type !== 'subform'
                              ) {
                                fieldStyle.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.4)' // Red glow
                              } else if (subFormFieldIndexes.includes(element.fieldId)) {
                                fieldStyle.boxShadow = '0 0 0 3px rgba(25, 135, 84, 0.4)' // Green glow
                              } else if (
                                usesPrefillEditFieldsMode(element) &&
                                element.type !== 'table'
                              ) {
                                fieldStyle.boxShadow = '0 0 0 3px rgba(255, 193, 7, 0.55)' // Amber glow
                                fieldStyle.backgroundColor = 'rgba(255, 193, 7, 0.08)'
                              }

                              return (
                                <div
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                  className='mb-4 p-2' // Generous space + padding for glow effect
                                  style={{
                                    ...providedDraggable.draggableProps.style,
                                    ...fieldStyle
                                  }}
                                  onClick={() => handleFieldClick(index)}
                                >
                                  {usesPrefillEditFieldsMode(element) &&
                                    element.type !== 'table' && (
                                      <div className='mb-2'>
                                        <span className='badge bg-warning text-dark'>
                                          Conditional edit ({element.prefillEditFields?.length || 0}
                                          )
                                        </span>
                                      </div>
                                    )}
                                  {element.type === 'heading' &&
                                    (() => {
                                      const HeadingTag = element.size || 'h2' // Safe default
                                      return (
                                        <HeadingTag
                                          className='fw-bold'
                                          style={{
                                            color: element.color,
                                            textAlign: element.alignment
                                          }}
                                        >
                                          {element.formHeading}
                                        </HeadingTag>
                                      )
                                    })()}
                                  {element.type === 'subheading' && (
                                    <p
                                      className='fs-5'
                                      style={{ color: element.color, textAlign: element.alignment }}
                                    >
                                      {element.formSubHeading}
                                    </p>
                                  )}

                                  {CANVAS_BASIC_INPUT_TYPES.includes(element.type) && (
                                    <>
                                      {!element.hideLabel && (
                                        <div className='d-flex justify-content-between align-items-center'>
                                          <label
                                            className={`fw-semibold mb-2 fs-5 ${
                                              !!element?.validation?.[0]?.rule.includes('required')
                                                ? 'required'
                                                : ''
                                            }`}
                                            style={{ color: element.color }}
                                          >
                                            {element.label}
                                            <RecipientNameLabelIcon element={element} />
                                          </label>
                                          {element.isTime && (
                                            <TimerDisplay timeString={element.time} />
                                          )}
                                        </div>
                                      )}
                                      {element.type === 'textarea' ? (
                                        <textarea
                                          className='form-control form-control-lg'
                                          rows={element.rows || 4}
                                          placeholder={element.placeholder}
                                          style={{ borderRadius: 8 }}
                                        />
                                      ) : (
                                        <input
                                          placeholder={element.placeholder}
                                          type={element.type}
                                          autoComplete='off'
                                          className='form-control form-control-lg'
                                          style={{ borderRadius: 8 }}
                                        />
                                      )}
                                    </>
                                  )}

                                  {element.type === 'select' && (
                                    <>
                                      <div className='d-flex justify-content-between align-items-center'>
                                        <label
                                          className={`fw-semibold mb-2 fs-5 ${
                                            !!element?.validation?.[0]?.rule.includes('required')
                                              ? 'required'
                                              : ''
                                          }`}
                                          style={{ color: element.color }}
                                        >
                                          {element.label}
                                        </label>
                                        {element.isTime && (
                                          <TimerDisplay timeString={element.time} />
                                        )}
                                      </div>
                                      <select
                                        className='form-select form-select-lg'
                                        name={element.name}
                                        style={{ borderRadius: 8 }}
                                      >
                                        {!!element?.options &&
                                          element.options?.map((option: any, i: number) => (
                                            <option
                                              key={option.id ?? `${element.fieldId}-opt-${i}`}
                                              value={option.value}
                                              selected={option.selected}
                                            >
                                              {option.text}
                                            </option>
                                          ))}
                                      </select>
                                    </>
                                  )}

                                  {element.type === 'formLogo' && (
                                    <div
                                      className={`d-flex justify-content-${
                                        element.alignment === 'right' ? 'end' : element.alignment
                                      }`}
                                    >
                                      <img
                                        src={element.imgSrc}
                                        alt='logo'
                                        style={{
                                          ...(Number(element.height) > 0
                                            ? { height: `${Number(element.height)}px` }
                                            : {}),
                                          ...(Number(element.width) > 0
                                            ? { width: `${Number(element.width)}px` }
                                            : { maxWidth: '150px' }),
                                          objectFit: 'contain'
                                        }}
                                      />
                                    </div>
                                  )}

                                  {(element.type === 'checkbox' || element.type === 'radio') && (
                                    <div>
                                      <div className='d-flex justify-content-between align-items-center'>
                                        <div
                                          className={`fw-semibold mb-3 fs-5 ${
                                            !!element?.validation?.[0]?.rule.includes('required')
                                              ? 'required'
                                              : ''
                                          }`}
                                          style={{ color: element.color }}
                                        >
                                          {element.label}
                                        </div>
                                        {element.isTime && (
                                          <TimerDisplay timeString={element.time} />
                                        )}
                                      </div>
                                      <div className='d-flex flex-column gap-6'>
                                        {!!element?.options &&
                                          element.options?.map((option: any, i: number) => (
                                            <div
                                              key={option.id ?? `${element.fieldId}-opt-${i}`}
                                              className='form-check'
                                            >
                                              <input
                                                type={element.type}
                                                className='form-check-input'
                                                id={`${element.name}-${option.value}`}
                                                value={option.value}
                                                checked={option.selected}
                                                name={`radio-group-${element.fieldId}`}
                                              />
                                              <label
                                                className='form-check-label ms-2'
                                                htmlFor={`${element.name}-${option.value}`}
                                              >
                                                {option.text}
                                              </label>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}

                                  {(element.type === 'switch' || element.type === 'subform') && (
                                    <div className='d-flex align-items-center justify-content-between'>
                                      <label
                                        className={`fw-semibold fs-5 ${
                                          !!element?.validation?.[0]?.rule.includes('required')
                                            ? 'required'
                                            : ''
                                        }`}
                                        style={{ color: element.color }}
                                      >
                                        {element.label}
                                      </label>
                                      <div className='form-check form-switch form-check-custom form-check-solid'>
                                        <input
                                          className='form-check-input h-30px w-50px'
                                          type='checkbox'
                                          checked={element.checked}
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {['file', 'datetime', 'date', 'time'].includes(element.type) && (
                                    <>
                                      <label
                                        className={`fw-semibold mb-2 fs-5 ${
                                          !!element?.validation?.[0]?.rule.includes('required')
                                            ? 'required'
                                            : ''
                                        }`}
                                        style={{ color: element.color }}
                                      >
                                        {element.label}
                                      </label>
                                      <input
                                        type={
                                          element.type === 'datetime'
                                            ? 'datetime-local'
                                            : element.type
                                        }
                                        className='form-control form-control-lg'
                                        onChange={
                                          element.type === 'file'
                                            ? (e) => handleFileUpload(index, e)
                                            : undefined
                                        }
                                        style={{ borderRadius: 8 }}
                                      />
                                    </>
                                  )}

                                  {element.type === 'daterange' && (
                                    <>
                                      <label
                                        className={`fw-semibold mb-2 fs-5 ${
                                          !!element?.validation?.[0]?.rule.includes('required')
                                            ? 'required'
                                            : ''
                                        }`}
                                        style={{ color: element.color }}
                                      >
                                        {element.label}
                                      </label>
                                      <div className='d-flex align-items-center gap-3'>
                                        <div className='flex-grow-1'>
                                          <label className='fs-7 text-muted'>Start Date</label>
                                          <input
                                            type='date'
                                            name='startDate'
                                            className='form-control form-control-lg'
                                            style={{ borderRadius: 8 }}
                                          />
                                        </div>
                                        <div className='pt-5'>–</div>
                                        <div className='flex-grow-1'>
                                          <label className='fs-7 text-muted'>End Date</label>
                                          <input
                                            type='date'
                                            name='endDate'
                                            className='form-control form-control-lg'
                                            style={{ borderRadius: 8 }}
                                          />
                                        </div>
                                      </div>
                                    </>
                                  )}

                                  {element.type === 'divider' && (
                                    <hr
                                      className='mb-2'
                                      style={{
                                        borderColor: element.color || '#dee2e6',
                                        borderWidth: `${element.thickness || 1}px`,
                                        borderStyle: 'solid',
                                        opacity: 1
                                      }}
                                    />
                                  )}

                                  {element.type === 'signature' && (
                                    <div className='d-grid'>
                                      <label
                                        className={`fw-semibold mb-2 fs-5 ${
                                          !!element?.validation?.[0]?.rule.includes('required')
                                            ? 'required'
                                            : ''
                                        }`}
                                        style={{ color: element.color }}
                                      >
                                        {element.label}
                                      </label>
                                      <div
                                        className='border rounded-3'
                                        style={{ overflow: 'hidden' }}
                                      >
                                        <SignatureCanvas
                                          penColor={element.color}
                                          backgroundColor={element.padColor}
                                          canvasProps={{
                                            width: element.width,
                                            height: element.height,
                                            className: 'sigCanvas w-100'
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {element.type === 'ratings' && (
                                    <Ratings
                                      validation={element.validation}
                                      label={element.label}
                                      color={element.color}
                                      alignment={element.alignment}
                                    />
                                  )}

                                  {element.type === 'table' && (
                                    <div>
                                      <label
                                        className={`fw-semibold fs-5 mb-2 ${
                                          !!element?.validation?.[0]?.rule.includes('required')
                                            ? 'required'
                                            : ''
                                        }`}
                                        style={{ color: element.color }}
                                      >
                                        {element.label}
                                      </label>
                                      <TableFieldPreview
                                        element={element}
                                        isPrefillForm={isPrefillForm}
                                        selectedColumnIndex={
                                          selectedField === index ? selectedTableColumnIndex : null
                                        }
                                        onColumnSelect={(columnIndex) => {
                                          setSelectedFields(index)
                                          onSelectTableColumn?.(columnIndex)
                                          onSelectTableRow?.(null)
                                        }}
                                        selectedRowIndex={
                                          selectedField === index ? selectedTableRowIndex : null
                                        }
                                        onRowSelect={(rowIndex) => {
                                          setSelectedFields(index)
                                          onSelectTableRow?.(rowIndex)
                                          onSelectTableColumn?.(null)
                                        }}
                                      />
                                    </div>
                                  )}

                                  {element.type === 'buttonRow' && (
                                    <div>
                                      <label className='fw-semibold fs-6 mb-2 text-muted d-block'>
                                        {element.label || 'Button row'}
                                      </label>
                                      <ButtonRowPreview element={element} />
                                    </div>
                                  )}

                                  {element.type === 'captcha' && (
                                    <div className='d-flex flex-column flex-sm-row align-items-start'>
                                      <label
                                        className={`fw-semibold fs-5 mb-2 mb-sm-0 me-sm-4 ${
                                          !!element?.validation?.[0]?.rule.includes('required')
                                            ? 'required'
                                            : ''
                                        } `}
                                      >
                                        {element.label}
                                      </label>
                                      <Captcha
                                        onRefresh={(c) => handleCaptchaRefresh(index, c)}
                                        inputTextName={element.inputTextName}
                                        inputTextNameColor={element.inputTextNameColor}
                                      />
                                    </div>
                                  )}

                                  {/* Validation Message */}
                                  {element?.validation?.[0]?.errorMessage &&
                                    element?.validation?.[0]?.rule.includes('required') && (
                                      <div
                                        className='mt-2'
                                        style={{
                                          color: element.validation[0].messageColor || '#f1416c'
                                        }}
                                      >
                                        {element.validation[0].errorMessage}
                                      </div>
                                    )}
                                </div>
                              )
                            }}
                          </Draggable>
                        )
                    )}

                    {/* Action Buttons Footer */}
                    <div className='d-flex justify-content-end gap-3 flex-wrap mt-10 pt-5 border-top'>
                      {safeFormElements.map((element: any, index: any) => {
                        if (!isStandaloneButton(element)) return null

                        const action =
                          element.buttonAction ||
                          (element.type === 'prevPageButton'
                            ? 'previous'
                            : element.type === 'nextPageButton'
                            ? 'next'
                            : 'submit')
                        const isPrev = action === 'previous'
                        const isNext = action === 'next'
                        const isSelected = selectedField === index

                        return (
                          <button
                            key={element.fieldId}
                            style={{
                              color: element.btnTxtColor,
                              backgroundColor: element.btnBgColor,
                              border: 'none',
                              boxShadow: isSelected ? '0 0 0 3px rgba(13, 110, 253, 0.4)' : 'none'
                            }}
                            type={element.buttonType || 'button'}
                            className='btn btn-lg fw-bold'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleFieldClick(index)
                            }}
                            disabled={
                              (isNext && currentPageIndex >= allFormElements.length - 1) ||
                              (isPrev && currentPageIndex <= 0)
                            }
                          >
                            {isPrev && <i className='bi bi-arrow-left me-2' />}
                            {element.text || 'Button'}
                            {isNext && <i className='bi bi-arrow-right ms-2' />}
                          </button>
                        )
                      })}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default FormPage
